// src/services/assetsAPI.ts
import axiosClient from './axiosClient';

// ---------------------- Types ----------------------

export type AssetStatus = 'available' | 'assigned' | 'maintenance' | 'retired';
export type AssetCondition = 'excellent' | 'good' | 'fair' | 'poor';

export interface Asset {
  id: number;
  name: string;
  asset_tag: string;
  serial_number: string;
  brand: string;
  model: string;
  description: string;
  status: AssetStatus;
  condition: AssetCondition;
  purchase_date: string;
  purchase_cost: string;
  purchase_invoice_number: string;
  vendor: string;
  warranty_expiry_date: string;
  location: string;
  company_id: number;
  category_id: number;
  current_holder_id: number;
  created_at: string;
  updated_at: string;
  category_name?: string;
  current_holder_name?: string;
  company_name?: string;
}

export interface CreateAssetData {
  name: string;
  asset_tag: string;
  serial_number: string;
  brand: string;
  model: string;
  description: string;
  status: AssetStatus;
  condition: AssetCondition;
  purchase_date: string;
  purchase_cost: number;
  purchase_invoice_number: string;
  vendor: string;
  warranty_expiry_date: string;
  location: string;
  company_id: number;
  category_id: number;
  current_holder_id: number;
}

export interface UpdateAssetData {
  name: string;
  asset_tag: string;
  serial_number: string;
  brand: string;
  model: string;
  description: string;
  status: string;
  condition: string;
  purchase_date: string;
  purchase_cost: number;
  purchase_invoice_number: string;
  vendor: string;
  warranty_expiry_date: string;
  location: string;
  current_holder_id: number;
}

export interface AssetsFilter {
  skip?: number;
  limit?: number;
  company_id?: number;
  status?: string;
}

export interface Category {
  id: number;
  name: string;
  description?: string;
  company_id: number;
  is_active: boolean;
  created_at: string;
}

// ---------------------- Request Types ----------------------

export interface AssetRequest {
  asset_id: number;
  category_id: number;
  request_note: string;
  priority: 'low' | 'medium' | 'high';
  id: number;
  user_id: number;
  company_id: number;
  status: string;
  created_at: string;
  updated_at: string;
  user_name?: string;
  asset_name?: string;
  category_name?: string;
  company_name?: string;
  approver_name?: string;
}

export interface CreateRequestData {
  asset_id: number;
  category_id: number;
  request_note: string;
  priority: 'low' | 'medium' | 'high';
}

// ---------------------- API Functions ----------------------

// ✅ FIXED: Remove /api/v1/ prefix from all endpoints

// GET /assets/ - Get Assets
export const getAssets = async (params?: AssetsFilter): Promise<Asset[]> => {
  const response = await axiosClient.get<Asset[]>('/assets/', {
    params: {
      skip: params?.skip || 0,
      limit: params?.limit || 100,
      company_id: params?.company_id,
      status: params?.status
    }
  });
  return response.data;
};

// POST /assets/ - Create Asset
export const createAsset = async (
  assetData: CreateAssetData
): Promise<Asset> => {
  const response = await axiosClient.post<Asset>('/assets/', assetData);
  return response.data;
};

// GET /assets/available/{category_id} or /assets/available/all - Get Available Assets
export const getAvailableAssets = async (
  categoryId: number | 'all',
  params?: {
    company_id?: number;
    include_all_categories?: boolean;
  }
): Promise<Asset[]> => {
  const response = await axiosClient.get<Asset[]>(
    `/assets/available/${categoryId}`,
    {
      params: {
        company_id: params?.company_id,
        include_all_categories: params?.include_all_categories
      }
    }
  );
  return response.data;
};

// GET /assets/my-devices - Get My Devices
export const getMyDevices = async (): Promise<Asset[]> => {
  const response = await axiosClient.get<Asset[]>('/assets/my-devices');
  return response.data;
};

// GET /assets/{asset_id} - Get Asset
export const getAsset = async (assetId: number): Promise<Asset> => {
  const response = await axiosClient.get<Asset>(`/assets/${assetId}`);
  return response.data;
};

// PUT /assets/{asset_id} - Update Asset
export const updateAsset = async (
  assetId: number,
  assetData: UpdateAssetData
): Promise<Asset> => {
  const response = await axiosClient.put<Asset>(
    `/assets/${assetId}`,
    assetData
  );
  return response.data;
};

// DELETE /assets/{asset_id} - Delete Asset
export const deleteAsset = async (assetId: number): Promise<string> => {
  const response = await axiosClient.delete<string>(`/assets/${assetId}`);
  return response.data;
};

// PUT /assets/{asset_id}/return - Return Asset
export interface ReturnAssetPayload {
  return_reason?: string;
  condition_at_return?: 'excellent' | 'good' | 'fair' | 'poor';
}

export const returnAsset = async (
  assetId: number,
  payload: ReturnAssetPayload
): Promise<Asset> => {
  const response = await axiosClient.put<Asset>(
    `/assets/${assetId}/return`,
    payload
  );
  return response.data;
};

// POST /assets/{asset_id}/assign-to-user - Directly Assign Asset to User
export interface AssignToUserPayload {
  user_id: number;
  condition_at_assignment?: 'excellent' | 'good' | 'fair' | 'poor';
}

export interface AssignmentResponse {
  message: string;
  assignment_id: number;
  asset_id: number;
  user_id: number;
  assigned_at: string;
}

export const assignAssetToUser = async (
  assetId: number,
  payload: AssignToUserPayload
): Promise<AssignmentResponse> => {
  const response = await axiosClient.post<AssignmentResponse>(
    `/assets/${assetId}/assign-to-user`,
    payload
  );
  return response.data;
};

// PUT /assets/{asset_id}/maintenance - Put Asset in Maintenance
export interface MaintenancePayload {
  maintenance_reason?: string;
}

export const putAssetInMaintenance = async (
  assetId: number,
  payload: MaintenancePayload
): Promise<Asset> => {
  const response = await axiosClient.put<Asset>(
    `/assets/${assetId}/maintenance`,
    payload
  );
  return response.data;
};

// PUT /assets/{asset_id}/complete-maintenance - Complete Maintenance
export interface CompleteMaintenancePayload {
  completion_notes?: string;
  completed_date?: string;
}

export const completeAssetMaintenance = async (
  assetId: number,
  payload: CompleteMaintenancePayload = {}
): Promise<Asset> => {
  const response = await axiosClient.put<Asset>(
    `/assets/${assetId}/complete-maintenance`,
    payload
  );
  return response.data;
};

// PUT /assets/{asset_id}/retire - Retire Asset
export interface RetireAssetPayload {
  retire_reason?: string;
}

export const retireAsset = async (
  assetId: number,
  payload: RetireAssetPayload
): Promise<Asset> => {
  const response = await axiosClient.put<Asset>(
    `/assets/${assetId}/retire`,
    payload
  );
  return response.data;
};

// PUT /assets/{asset_id}/reactivate - Reactivate Asset
export const reactivateAsset = async (assetId: number): Promise<Asset> => {
  const response = await axiosClient.put<Asset>(
    `/assets/${assetId}/reactivate`
  );
  return response.data;
};

// GET /categories/ - Get Categories (for dropdowns)
export const getCategories = async (
  companyId?: number
): Promise<Category[]> => {
  const params: { skip: number; limit: number; company_id?: number } = {
    skip: 0,
    limit: 1000
  };

  if (companyId) {
    params.company_id = companyId;
  }

  const response = await axiosClient.get<Category[]>('/categories/', {
    params
  });
  return response.data;
};

// ✅ NEW: POST /categories/ - Create Category
export const createCategory = async (categoryData: {
  name: string;
  description?: string;
  company_id: number;
}): Promise<Category> => {
  const response = await axiosClient.post<Category>(
    '/categories/',
    categoryData
  );
  return response.data;
};

// ✅ NEW: POST /categories/bulk/all-companies - Create Category for All Companies
export const createBulkCategoryAllCompanies = async (categoryData: {
  name: string;
  description?: string;
}): Promise<Category[]> => {
  const response = await axiosClient.post<Category[]>(
    '/categories/bulk/all-companies',
    categoryData
  );
  return response.data;
};

// ✅ NEW: PUT /categories/{category_id} - Update Category
export const updateCategory = async (
  categoryId: number,
  categoryData: { name: string; description?: string }
): Promise<Category> => {
  const response = await axiosClient.put<Category>(
    `/categories/${categoryId}`,
    categoryData
  );
  return response.data;
};

// ✅ NEW: GET /categories/{category_id} - Get Category
export const getCategory = async (categoryId: number): Promise<Category> => {
  const response = await axiosClient.get<Category>(`/categories/${categoryId}`);
  return response.data;
};

// ✅ NEW: DELETE /categories/{category_id} - Delete Category
export const deleteCategory = async (categoryId: number): Promise<string> => {
  const response = await axiosClient.delete<string>(
    `/categories/${categoryId}`
  );
  return response.data;
};

// ---------------------- Request API Functions ----------------------

// POST /requests/ - Create Asset Request
export const createAssetRequest = async (
  requestData: CreateRequestData
): Promise<AssetRequest> => {
  const response = await axiosClient.post<AssetRequest>(
    '/requests/',
    requestData
  );
  return response.data;
};

// GET /requests/my-requests - Get My Requests
export const getMyRequests = async (params?: {
  skip?: number;
  limit?: number;
  status?: string;
}): Promise<AssetRequest[]> => {
  const response = await axiosClient.get<AssetRequest[]>(
    '/requests/my-requests',
    {
      params: {
        skip: params?.skip || 0,
        limit: params?.limit || 100,
        status: params?.status
      }
    }
  );
  return response.data;
};

// PUT /requests/{request_id}/cancel - Enhanced Cancel Request
export const cancelRequest = async (
  requestId: number,
  cancellationReason?: string
): Promise<{ message: string }> => {
  const url = cancellationReason
    ? `/requests/${requestId}/cancel?cancellation_reason=${encodeURIComponent(
        cancellationReason
      )}`
    : `/requests/${requestId}/cancel`;

  const response = await axiosClient.put<{ message: string }>(url);
  return response.data;
};

// PUT /requests/{request_id}/on-hold - Put Request On Hold
export interface PutOnHoldPayload {
  hold_reason: string;
}

export const putOnHold = async (
  requestId: number,
  payload: PutOnHoldPayload
): Promise<{ message: string }> => {
  const response = await axiosClient.put<{ message: string }>(
    `/requests/${requestId}/on-hold`,
    {}, // Empty body
    {
      params: {
        hold_reason: payload.hold_reason
      }
    }
  );
  return response.data;
};

// PUT /requests/{request_id}/resume - Resume Request
export const resumeRequest = async (
  requestId: number
): Promise<{ message: string }> => {
  const response = await axiosClient.put<{ message: string }>(
    `/requests/${requestId}/resume`
  );
  return response.data;
};

// ✅ ADDED: GET /requests/ - Get All Asset Requests (for admin)
export const getAllRequests = async (params?: {
  skip?: number;
  limit?: number;
  status?: string;
  company_id?: number;
}): Promise<AssetRequest[]> => {
  const response = await axiosClient.get<AssetRequest[]>('/requests/', {
    params: {
      skip: params?.skip || 0,
      limit: params?.limit || 100,
      status: params?.status,
      company_id: params?.company_id
    }
  });
  return response.data;
};

// ✅ ADDED: GET /requests/{request_id} - Get Single Request
export const getRequest = async (requestId: number): Promise<AssetRequest> => {
  const response = await axiosClient.get<AssetRequest>(
    `/requests/${requestId}`
  );
  return response.data;
};

export default {
  getAssets,
  getMyDevices,
  getAvailableAssets,
  getAsset,
  createAsset,
  updateAsset,
  deleteAsset,
  returnAsset,
  assignAssetToUser,
  putAssetInMaintenance,
  completeAssetMaintenance,
  retireAsset,
  reactivateAsset,
  getCategories,
  createCategory,
  createBulkCategoryAllCompanies,
  updateCategory,
  getCategory,
  deleteCategory,
  createAssetRequest,
  getMyRequests,
  cancelRequest,
  putOnHold,
  resumeRequest,
  getAllRequests,
  getRequest
};
